import React, { useRef, useState } from "react";
import { FormHandles } from "@unform/core";

import { ILoginModel } from "../../model/login";
import { getSignupUrl } from "../../helpers/navigation-helper";
import BrandPanel from "./BrandPanel";
import AuthInput from "./AuthInput";
import { submitLogin, submitRecover } from "./actions";
import {
  Screen,
  FormPane,
  FormCard,
  Header,
  Form,
  LabelLink,
  FormError,
  PrimaryButton,
  FootNote,
  ConfirmationCard,
  ConfirmationIcon,
  ConfirmationTitle,
  ConfirmationText,
} from "./styles";

export type LoginMode = "login" | "recover" | "sent";

type LoginProps = {
  initialMode?: LoginMode;
};

const HEADINGS: Record<LoginMode, { heading: string; subheading: string }> = {
  login: {
    heading: "Acesse sua conta",
    subheading: "Bem-vindo de volta! Entre para gerenciar sua corretora.",
  },
  recover: {
    heading: "Recuperar senha",
    subheading:
      "Informe seu e-mail e enviaremos um link para redefinir sua senha.",
  },
  sent: {
    heading: "Verifique seu e-mail",
    subheading: "Enviamos as instruções de redefinição de senha.",
  },
};

const Login: React.FC<LoginProps> = ({ initialMode = "login" }) => {
  const signupUrl = getSignupUrl();
  const [mode, setMode] = useState<LoginMode>(initialMode);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setLoading] = useState(false);

  const loginFormRef = useRef<FormHandles>(null);
  const recoverFormRef = useRef<FormHandles>(null);

  const { heading, subheading } = HEADINGS[mode];

  const changeMode = (nextMode: LoginMode) => {
    setErrorMessage("");
    setLoading(false);
    setMode(nextMode);
  };

  const handleLoginSubmit = (data: ILoginModel) => {
    setLoading(true);
    setErrorMessage("");

    submitLogin(data, loginFormRef, {
      onError: setErrorMessage,
      onFinish: () => setLoading(false),
    });
  };

  const handleRecoverSubmit = (data: { email: string }) => {
    setLoading(true);
    setErrorMessage("");

    submitRecover(data, recoverFormRef, {
      onError: setErrorMessage,
      onFinish: () => setLoading(false),
      onSent: () => changeMode("sent"),
    });
  };

  return (
    <Screen>
      <BrandPanel />

      <FormPane>
        <FormCard>
          <Header>
            <h2>{heading}</h2>
            <p>{subheading}</p>
          </Header>

          {mode === "login" && (
            <>
              <Form ref={loginFormRef} onSubmit={handleLoginSubmit}>
                <AuthInput
                  name="email"
                  label="E-mail"
                  type="email"
                  placeholder="voce@corretora.com.br"
                  autoComplete="email"
                />
                <AuthInput
                  name="password"
                  label="Senha"
                  password
                  placeholder="Digite sua senha"
                  autoComplete="current-password"
                  labelAction={
                    <LabelLink
                      onClick={(event) => {
                        event.preventDefault();
                        changeMode("recover");
                      }}
                    >
                      Esqueci minha senha
                    </LabelLink>
                  }
                />
                {errorMessage && <FormError>{errorMessage}</FormError>}
                <PrimaryButton type="submit" disabled={isLoading}>
                  {isLoading ? "Entrando..." : "Entrar"}
                </PrimaryButton>
              </Form>

              <FootNote>
                Ainda não tem conta?{" "}
                <a href={signupUrl} target="_blank" rel="noreferrer">
                  Assine agora
                </a>
              </FootNote>
            </>
          )}

          {mode === "recover" && (
            <>
              <Form ref={recoverFormRef} onSubmit={handleRecoverSubmit}>
                <AuthInput
                  name="email"
                  label="E-mail"
                  type="email"
                  placeholder="voce@corretora.com.br"
                  autoComplete="email"
                />
                {errorMessage && <FormError>{errorMessage}</FormError>}
                <PrimaryButton type="submit" disabled={isLoading}>
                  {isLoading ? "Enviando..." : "Recuperar senha"}
                </PrimaryButton>
              </Form>

              <FootNote>
                <a
                  href="/login"
                  onClick={(event) => {
                    event.preventDefault();
                    changeMode("login");
                  }}
                >
                  ← Voltar para o login
                </a>
              </FootNote>
            </>
          )}

          {mode === "sent" && (
            <>
              <ConfirmationCard>
                <ConfirmationIcon>✓</ConfirmationIcon>
                <ConfirmationTitle>E-mail enviado!</ConfirmationTitle>
                <ConfirmationText>
                  Enviamos um link de redefinição de senha para o seu e-mail.
                  Verifique a caixa de entrada e o spam.
                </ConfirmationText>
              </ConfirmationCard>

              <FootNote>
                <a
                  href="/login"
                  onClick={(event) => {
                    event.preventDefault();
                    changeMode("login");
                  }}
                >
                  ← Voltar para o login
                </a>
              </FootNote>
            </>
          )}
        </FormCard>
      </FormPane>
    </Screen>
  );
};

export default Login;
